// Production → Warehouse transfer queue. Sits under "Project Bobin State" on
// the project-monitor page. Same AdvanceTable system as Project Bobin State.
// Lists drums/pallets about to move from production into the warehouse, with a
// per-row "..." action menu: transfer (bridge a production drum into the yard),
// edit length, put away (reception tablet), view on map.
import { useMemo, useState, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { TimeSince } from '../../components/machines/EstimateCell.js';
import {
  Badge,
  Button,
  Dropdown,
  Modal,
  Form,
  Spinner,
  Alert
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faWarehouse,
  faPenToSquare,
  faTruckRampBox,
  faMap,
  faRotate,
  faArrowRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import SearchBox from '../../components/common/SearchBox';
import { drumSpecLabel } from '../../features/warehouse/warehouseUtils.js';
import AddPhaseDialog from '../../components/production/AddPhaseDialog';
import { API } from '../../config/api';

const http = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

const COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  GRAY: '#808080', BLACK: '#333', WHITE: '#FFFFFF', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316', NATUR: '#E8DCC4', 'GREY 7004': '#8C9192'
};

// White on a white card is nothing at all, and it is a real sheath colour —
// MYYUP is sold in it. The swatch gets a border so it reads as a colour rather
// than as a gap.
const swatchStyle = hex => ({
  width: 10,
  height: 10,
  borderRadius: 2,
  background: hex,
  display: 'inline-block',
  border: '1px solid rgba(128,128,128,.55)',
  flex: '0 0 auto'
});

// Where the thing physically is. Grey said "production" and grey also said
// "nothing to report", so the one state that means "still on the factory floor,
// not received yet" was the least visible cell in the table.
const WHERE_BADGE = {
  production: { bg: 'warning', label: 'Production' },
  yard: { bg: 'success', label: 'Yard' },
  slot: { bg: 'primary', label: 'Racked' },
  truck: { bg: 'info', label: 'On truck' },
  shipped: { bg: 'dark', label: 'Shipped' }
};

function useTransferPending() {
  return useQuery({
    queryKey: ['warehouse', 'transfer-pending'],
    queryFn: async () =>
      (await http.get('/api/v1/warehouse/reception/pending')).data?.data || [],
    refetchInterval: 20000,
    staleTime: 10000
  });
}

// "..." menu rendered via a custom toggle so it matches the reveal-style buttons.
// forwardRef is required for react-bootstrap Dropdown positioning.
const DotsToggle = forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-link text-body p-0 border-0"
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
    aria-label="actions"
  >
    <FontAwesomeIcon icon={faEllipsisVertical} />
  </button>
));

const WarehouseTransferTable = () => {
  const { data: pending = [], isLoading, error, refetch, isFetching } = useTransferPending();
  const [search, setSearch] = useState('');
  const [busyCode, setBusyCode] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editLength, setEditLength] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  // Changed our mind: this drum is not finished after all. The dialog is shared
  // with the yard in Factory 3D — same endpoint, same wording, one copy.
  const [phaseRow, setPhaseRow] = useState(null);

  const flash = (variant, msg) => {
    setBanner({ variant, msg });
    setTimeout(() => setBanner(null), 4000);
  };

  // Reconcile the warehouse against production reality: bridge any finished drums
  // the live (fire-and-forget) auto-bridge missed, and surface orphans.
  const handleSync = async () => {
    setSyncBusy(true);
    setSyncReport(null);
    try {
      const res = await http.post('/api/v1/warehouse/sync');
      const r = res.data?.report || {};
      setSyncReport(r);
      flash(
        r.orphans > 0 ? 'warning' : 'success',
        `Sync complete — ${r.bridged} bridged, ${r.alreadyPresent} already present, ${r.orphans} orphan(s).`
      );
      refetch();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    } finally {
      setSyncBusy(false);
    }
  };

  // Bridge a production drum into the yard (becomes a StorageItem).
  const transfer = async row => {
    setBusyCode(row.code);
    try {
      const res = await http.post('/api/v1/warehouse/reception/scan', { code: row.code });
      flash('success', `${res.data?.data?.item?.serial || row.code} transferred to warehouse`);
      refetch();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    } finally {
      setBusyCode(null);
    }
  };

  const openEdit = row => {
    setEditRow(row);
    setEditLength(String(row.lengthMeters ?? ''));
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const len = Number(String(editLength).replace(',', '.'));
    if (!(len >= 0)) {
      flash('danger', 'Enter a valid length');
      return;
    }
    setEditBusy(true);
    try {
      let serial = editRow.serial;
      // a production drum isn't a StorageItem yet — transfer (bridge) first
      if (!serial) {
        const res = await http.post('/api/v1/warehouse/reception/scan', { code: editRow.code });
        serial = res.data?.data?.item?.serial;
      }
      if (!serial) throw new Error('Could not resolve the item');
      await http.patch(`/api/v1/warehouse/items/${serial}`, { lengthMeters: len });
      flash('success', `${serial} length set to ${len} m`);
      setEditRow(null);
      refetch();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pending
      .map(r => ({
        ...r,
        displayId: r.tokenId || r.serial || r.code,
        inProduction: r.status === 'new' || r.locationKind === 'production'
      }))
      .filter(r =>
        !q
          ? true
          : `${r.cableName} ${r.displayId} ${r.job} ${r.type} ${r.customerName || 'stock'}`
              .toLowerCase()
              .includes(q)
      );
  }, [pending, search]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'displayId',
        header: 'Token / Serial',
        cell: ({ row: { original } }) => (
          <span className="fw-semibold font-monospace fs-9">{original.displayId}</span>
        ),
        meta: { headerProps: { style: { minWidth: 150 } }, cellProps: { className: 'py-2' } }
      },
      {
        accessorKey: 'cableName',
        header: 'Product',
        cell: ({ row: { original } }) => {
          const hex = COLOR_HEX[String(original.color || '').toUpperCase()];
          // A europallet is not one product, it is what was stacked on it. The
          // name used to read "FY 1x4 (+1)" — one label and a count of the ones
          // it would not name, which is the least useful way to say it. The
          // composition is what a receiver checks against the physical pallet.
          const parts = original.contents || [];
          return (
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                {hex && (
                  <span
                    style={swatchStyle(hex)}
                    title={original.color}
                  />
                )}
                <span className="fw-semibold text-body">{original.cableName}</span>
              </div>
              {parts.length > 1 && (
                <div className="fs-10 text-700 lh-sm">
                  {parts.map((c, i) => (
                    <div key={i} className="white-space-nowrap">
                      <span className="text-body-tertiary">·</span>{' '}
                      {c.coilCount ? `${c.coilCount} × ` : ''}
                      {c.productLabel || '—'}
                      {c.lengthMeters
                        ? ` — ${Math.round(c.lengthMeters).toLocaleString()} m`
                        : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        },
        meta: { headerProps: { style: { minWidth: 200 } } }
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <Badge
              bg={original.type === 'europallet' ? 'primary' : 'warning'}
              variant="phoenix"
              className="align-self-start"
            >
              {original.type === 'europallet' ? 'PALLET' : 'DRUM'}
            </Badge>
            {drumSpecLabel(original) && (
              <span className="text-body-tertiary text-capitalize fs-10 mt-1">
                {drumSpecLabel(original)}
              </span>
            )}
          </div>
        ),
        meta: { headerProps: { style: { width: 120 } } }
      },
      {
        accessorKey: 'lengthMeters',
        header: 'Length',
        cell: ({ row: { original } }) => `${Number(original.lengthMeters || 0).toLocaleString()} m`,
        meta: { headerProps: { style: { width: 110 }, className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'weightKg',
        header: 'Weight',
        cell: ({ row: { original } }) =>
          original.weightKg ? `${Number(original.weightKg).toLocaleString()} kg` : '—',
        meta: { headerProps: { style: { width: 110 }, className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'customerName',
        header: 'For',
        cell: ({ row: { original } }) =>
          original.customerName ? (
            <div className="d-flex flex-column">
              <Badge bg="primary" variant="phoenix" className="align-self-start">
                {original.customerName}
              </Badge>
              {original.salesOrderCode && (
                <span className="fs-10 text-body-tertiary font-monospace mt-1">
                  {original.salesOrderCode}
                </span>
              )}
            </div>
          ) : (
            // Grey read as "no data". Stock IS the answer, not the absence of
            // one — the drum is ours until somebody buys it.
            <Badge bg="info" variant="phoenix">
              STOCK
            </Badge>
          ),
        meta: { headerProps: { style: { minWidth: 130 } } }
      },
      {
        accessorKey: 'job',
        header: 'Job',
        cell: ({ row: { original } }) => (
          <span className="font-monospace fs-10 text-body-tertiary">{original.job || '—'}</span>
        ),
        meta: { headerProps: { style: { minWidth: 110 } } }
      },
      {
        id: 'finishedAt',
        header: 'Available since',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <TimeSince ts={original.finishedAt} label="finished" />
        )
      },
      {
        accessorKey: 'locationKind',
        header: 'Where',
        cell: ({ row: { original } }) => {
          const kind = original.inProduction
            ? 'production'
            : original.locationKind || 'yard';
          const b = WHERE_BADGE[kind] || { bg: 'secondary', label: kind };
          return (
            <div className="d-flex flex-column align-items-start gap-1">
              <Badge bg={b.bg} variant="phoenix">
                {b.label}
              </Badge>
              {kind === 'production' && (
                <span
                  className="fs-10 text-body-tertiary"
                  title="Transfer it (or press Sync warehouse) to move it into the yard"
                >
                  not received yet
                </span>
              )}
            </div>
          );
        },
        meta: { headerProps: { style: { width: 130 } } }
      },
      {
        id: 'action',
        header: '',
        cell: ({ row: { original } }) => (
          <Dropdown align="end">
            <Dropdown.Toggle as={DotsToggle} />
            {/* The table body scrolls (AdvanceTable wraps it in .scrollbar), and
                an absolutely-positioned menu inside a scroll container is
                clipped by it — the menu opened and could not be seen. Floating
                it (strategy 'fixed') takes it out of that box; renderOnMount is
                what Popper needs to measure it before the first open. */}
            <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }}>
              {original.inProduction && (
                <Dropdown.Item
                  onClick={() => transfer(original)}
                  disabled={busyCode === original.code}
                >
                  <FontAwesomeIcon icon={faWarehouse} className="me-2" />
                  Transfer to warehouse
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => openEdit(original)}>
                <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
                Edit length…
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => setPhaseRow(original)}
                disabled={!original.jobId || original.type === 'europallet'}
                title={
                  original.type === 'europallet'
                    ? 'A pallet holds coils from several jobs — unpack it at the Packing Station'
                    : !original.jobId
                      ? 'No job attached to this row'
                      : undefined
                }
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} className="me-2" />
                Add phase…
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item as={Link} to="/test-interfaces/warehouse-reception">
                <FontAwesomeIcon icon={faTruckRampBox} className="me-2" />
                Put away…
              </Dropdown.Item>
              <Dropdown.Item as={Link} to="/apps/warehouse-management/map">
                <FontAwesomeIcon icon={faMap} className="me-2" />
                View on map
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ),
        meta: { headerProps: { className: 'text-end' }, cellProps: { className: 'text-end' } }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyCode]
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div className="mb-2">
      {/* flex-wrap: five items on one line do not fit a narrow container, and
          without it the ms-auto button simply hung off the right edge instead
          of dropping to the next line. */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <FontAwesomeIcon icon={faWarehouse} className="text-info fs-5" />
        <h2 className="mb-0">Production → Warehouse</h2>
        <Badge bg="info" variant="phoenix">
          {rows.length} pending
        </Badge>
        <Button variant="link" size="sm" className="text-body" onClick={() => refetch()}>
          <FontAwesomeIcon icon={faRotate} spin={isFetching} className="me-1" /> Refresh
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-auto"
          onClick={handleSync}
          disabled={syncBusy}
          title="Reconcile the warehouse with production — bridge any finished drums that were missed"
        >
          <FontAwesomeIcon icon={faRotate} spin={syncBusy} className="me-1" />
          {syncBusy ? 'Syncing…' : 'Sync warehouse'}
        </Button>
      </div>

      {banner && <Alert variant={banner.variant} className="py-2">{banner.msg}</Alert>}

      {syncReport && (
        <Alert
          variant={syncReport.orphans > 0 ? 'warning' : 'info'}
          className="py-2"
          dismissible
          onClose={() => setSyncReport(null)}
        >
          <div className="fw-semibold mb-1">
            Warehouse sync — {syncReport.productionFinalDrums} finished drum(s) in production
          </div>
          <div className="d-flex flex-wrap gap-3 fs-9">
            <span className="text-success">✓ {syncReport.bridged} bridged</span>
            <span className="text-body-tertiary">• {syncReport.alreadyPresent} already present</span>
            {syncReport.failed > 0 && (
              <span className="text-danger">✕ {syncReport.failed} failed</span>
            )}
            <span className={syncReport.orphans > 0 ? 'text-warning fw-semibold' : 'text-body-tertiary'}>
              ⚠ {syncReport.orphans} orphan(s)
            </span>
          </div>
          {syncReport.orphans > 0 && (
            <div className="mt-1 fs-10 text-body-tertiary">
              Orphans (on-hand, production no longer reports the token — check manually):{' '}
              {(syncReport.orphanItems || [])
                .slice(0, 8)
                .map(o => `${o.serial} (${o.productLabel || '?'})`)
                .join(', ')}
              {syncReport.orphans > 8 ? ` +${syncReport.orphans - 8} more` : ''}
            </div>
          )}
        </Alert>
      )}

      {isLoading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" size="sm" /> Loading…
        </div>
      ) : error ? (
        <Alert variant="danger">{String(error.message)}</Alert>
      ) : (
        <AdvanceTableProvider {...table}>
          <div className="mb-3" style={{ maxWidth: 360 }}>
            <SearchBox placeholder="Search…" onChange={e => setSearch(e.target.value || '')} />
          </div>
          <div className="bg-body-emphasis rounded-2 border border-translucent px-3">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-8' }} />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
      )}

      <Modal show={!!editRow} onHide={() => setEditRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-7">Edit length — {editRow?.displayId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-body-tertiary fs-9">
            {editRow?.cableName} · {editRow?.type === 'europallet' ? 'pallet' : 'drum'}
            {editRow && !editRow.serial && (
              <span className="d-block text-warning mt-1">
                This drum is still in production — saving will also transfer it to the warehouse.
              </span>
            )}
          </p>
          <Form.Group>
            <Form.Label>Length (m)</Form.Label>
            <Form.Control
              type="number"
              value={editLength}
              autoFocus
              onChange={e => setEditLength(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveEdit()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditRow(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={editBusy}>
            {editBusy ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <AddPhaseDialog
        target={phaseRow}
        onHide={() => setPhaseRow(null)}
        onDone={({ variant, message }) => {
          flash(variant, message);
          refetch();
        }}
      />
    </div>
  );
};

export default WarehouseTransferTable;
